import React, {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import './MagicTimer.css';

/* ------------------------------------------------------------------ */
/*  Constants & helpers                                                */
/* ------------------------------------------------------------------ */

const STORAGE_KEY = 'mtg-commander-timer-v1';
const MIN_PLAYERS = 2;
const MAX_PLAYERS = 6;

const PALETTE = [
  '#e5484d', // red
  '#3e63dd', // blue
  '#30a46c', // green
  '#f5c518', // yellow
  '#8e4ec6', // purple
  '#f76b15', // orange
  '#d6409f', // pink
  '#12a594', // teal
];

const DEFAULT_SETTINGS = {
  startSec: 180, // length of the "Start" timer
  finalSec: 180, // time added by "Final"
  penalty: 5, // life lost if the Final time runs out
  startLife: 40,
  sound: true,
};

const uid = () => Math.random().toString(36).slice(2, 9);
const pad = (n) => String(n).padStart(2, '0');
const fmtClock = (sec) => `${Math.floor(sec / 60)}:${pad(sec % 60)}`;

// Pick black or white text depending on how bright the background color is.
function textOn(hex) {
  const n = parseInt(hex.slice(1), 16);
  const r = n >> 16;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? '#111' : '#fff';
}

function makePlayer(index, color, life) {
  return { id: uid(), name: `Player ${index + 1}`, color, life, out: false };
}

const idleTimer = () => ({
  turnPhase: 'waiting', // 'waiting' (before Start is pressed) | 'active'
  paused: false,
  deadline: null, // ms timestamp when the clock hits zero (while running)
  remainingMs: 0, // ms left (while paused)
  finalUsed: false,
});

function defaultState() {
  return {
    phase: 'setup', // 'setup' | 'playing'
    settings: { ...DEFAULT_SETTINGS },
    players: Array.from({ length: 4 }, (_, i) =>
      makePlayer(i, PALETTE[i], DEFAULT_SETTINGS.startLife)
    ),
    turn: 0,
    ...idleTimer(),
    notice: null,
  };
}

/* ------------------------------------------------------------------ */
/*  Game logic (pure reducer)                                          */
/* ------------------------------------------------------------------ */

function nextAliveIndex(players, from) {
  for (let i = 1; i <= players.length; i++) {
    const idx = (from + i) % players.length;
    if (!players[idx].out) return idx;
  }
  return from;
}

function advance(state) {
  return {
    ...state,
    turn: nextAliveIndex(state.players, state.turn),
    ...idleTimer(),
  };
}

function expire(state) {
  const cur = state.players[state.turn];

  // Start timer ran out without Final: just pass the turn.
  if (!state.finalUsed) {
    const next = advance(state);
    return {
      ...next,
      notice: {
        id: uid(),
        kind: 'timeout',
        name: cur.name,
        nextName: next.players[next.turn].name,
      },
    };
  }

  // Final timer ran out: lose life, then pass the turn.
  const to = Math.max(0, cur.life - state.settings.penalty);
  const players = state.players.map((p, i) =>
    i === state.turn ? { ...p, life: to, out: p.out || to <= 0 } : p
  );
  const next = advance({ ...state, players });
  return {
    ...next,
    notice: {
      id: uid(),
      kind: 'penalty',
      name: cur.name,
      from: cur.life,
      to,
      amount: state.settings.penalty,
      eliminated: to <= 0,
      nextName: next.players[next.turn].name,
    },
  };
}

function reducer(state, action) {
  switch (action.type) {
    /* ---- setup ---- */
    case 'SET_COUNT': {
      const n = Math.min(MAX_PLAYERS, Math.max(MIN_PLAYERS, action.count));
      const players = state.players.slice(0, n);
      while (players.length < n) {
        const used = players.map((p) => p.color);
        const color = PALETTE.find((c) => !used.includes(c)) || PALETTE[0];
        players.push(makePlayer(players.length, color, state.settings.startLife));
      }
      return { ...state, players };
    }
    case 'SET_NAME':
      return {
        ...state,
        players: state.players.map((p) =>
          p.id === action.id ? { ...p, name: action.name } : p
        ),
      };
    case 'SET_COLOR': {
      const target = state.players.find((p) => p.id === action.id);
      const other = state.players.find(
        (p) => p.color === action.color && p.id !== action.id
      );
      return {
        ...state,
        players: state.players.map((p) => {
          if (p.id === action.id) return { ...p, color: action.color };
          if (other && p.id === other.id) return { ...p, color: target.color };
          return p;
        }),
      };
    }
    case 'SET_SETTINGS':
      return { ...state, settings: { ...state.settings, ...action.patch } };

    /* ---- game lifecycle ---- */
    case 'START_GAME':
    case 'RESTART':
      return {
        ...state,
        phase: 'playing',
        turn: 0,
        ...idleTimer(),
        notice: null,
        players: state.players.map((p, i) => ({
          ...p,
          name: p.name.trim() || `Player ${i + 1}`,
          life: state.settings.startLife,
          out: false,
        })),
      };
    case 'EDIT_PLAYERS':
      return { ...state, phase: 'setup', turn: 0, ...idleTimer(), notice: null };

    /* ---- turn & timer ---- */
    case 'START_TURN':
      if (state.turnPhase !== 'waiting') return state;
      return {
        ...state,
        turnPhase: 'active',
        paused: false,
        deadline: action.now + state.settings.startSec * 1000,
        remainingMs: 0,
        finalUsed: false,
      };
    case 'FINAL': {
      if (state.turnPhase !== 'active' || state.finalUsed) return state;
      const extra = state.settings.finalSec * 1000;
      return state.paused
        ? { ...state, finalUsed: true, remainingMs: state.remainingMs + extra }
        : { ...state, finalUsed: true, deadline: state.deadline + extra };
    }
    case 'PAUSE':
      if (state.turnPhase !== 'active' || state.paused) return state;
      return {
        ...state,
        paused: true,
        remainingMs: Math.max(0, state.deadline - action.now),
        deadline: null,
      };
    case 'RESUME':
      if (state.turnPhase !== 'active' || !state.paused) return state;
      return {
        ...state,
        paused: false,
        deadline: action.now + state.remainingMs,
        remainingMs: 0,
      };
    case 'END_TURN':
      if (state.turnPhase !== 'active') return state;
      return advance(state);
    case 'SET_TURN':
      if (state.turnPhase !== 'waiting' || state.players[action.index].out) {
        return state;
      }
      return { ...state, turn: action.index };
    case 'TICK':
      if (
        state.turnPhase !== 'active' ||
        state.paused ||
        action.now < state.deadline
      ) {
        return state; // same reference => no re-render from the reducer
      }
      return expire(state);

    /* ---- life ---- */
    case 'ADJUST_LIFE': {
      if (state.phase !== 'playing') return state;
      const idx = state.players.findIndex((p) => p.id === action.id);
      const p = state.players[idx];
      if (!p || p.out) return state;
      const life = Math.max(0, p.life + action.delta);
      const players = state.players.map((q, i) =>
        i === idx ? { ...q, life, out: life <= 0 } : q
      );
      const next = { ...state, players };
      return life <= 0 && idx === state.turn ? advance(next) : next;
    }
    case 'TOGGLE_OUT': {
      const idx = state.players.findIndex((p) => p.id === action.id);
      const p = state.players[idx];
      if (!p) return state;
      if (p.out) {
        // Revive
        return {
          ...state,
          players: state.players.map((q, i) =>
            i === idx ? { ...q, out: false, life: Math.max(1, q.life) } : q
          ),
        };
      }
      const next = {
        ...state,
        players: state.players.map((q, i) =>
          i === idx ? { ...q, out: true } : q
        ),
      };
      return idx === state.turn ? advance(next) : next;
    }

    case 'DISMISS_NOTICE':
      return { ...state, notice: null };

    default:
      return state;
  }
}

/* ------------------------------------------------------------------ */
/*  Persistence (localStorage)                                         */
/* ------------------------------------------------------------------ */

function saveState(state) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ state, savedAt: Date.now() })
    );
  } catch (e) {
    /* storage unavailable (private mode etc.) - ignore */
  }
}

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState();
    const { state, savedAt } = JSON.parse(raw);
    if (!state || !Array.isArray(state.players) || state.players.length < 1) {
      return defaultState();
    }
    const s = {
      ...defaultState(),
      ...state,
      settings: { ...DEFAULT_SETTINGS, ...state.settings },
      notice: null,
    };
    if (!s.players[s.turn]) s.turn = 0;
    // If the page was closed/reloaded mid-turn, come back paused with the
    // time that was left at the last save, instead of silently burning time.
    if (s.turnPhase === 'active' && !s.paused) {
      s.paused = true;
      s.remainingMs = Math.max(0, (s.deadline || 0) - (savedAt || Date.now()));
      s.deadline = null;
    }
    return s;
  } catch (e) {
    return defaultState();
  }
}

/* ------------------------------------------------------------------ */
/*  Small UI pieces                                                    */
/* ------------------------------------------------------------------ */

function NumberField({ label, suffix, value, min, max, step, onChange }) {
  const [text, setText] = useState(String(value));
  return (
    <label className="mt-field">
      <span className="mt-field-label">{label}</span>
      <span className="mt-field-input">
        <input
          type="number"
          inputMode="decimal"
          value={text}
          min={min}
          max={max}
          step={step}
          onChange={(e) => {
            setText(e.target.value);
            const n = parseFloat(e.target.value);
            if (!Number.isNaN(n) && n >= min && n <= max) onChange(n);
          }}
          onBlur={() => setText(String(value))}
        />
        <span className="mt-suffix">{suffix}</span>
      </span>
    </label>
  );
}

function SettingsFields({ settings, onChange }) {
  const toMin = (sec) => Math.round((sec / 60) * 100) / 100;
  return (
    <div className="mt-fields">
      <NumberField
        label="Start timer"
        suffix="min"
        value={toMin(settings.startSec)}
        min={0.25}
        max={30}
        step={0.25}
        onChange={(v) => onChange({ startSec: Math.round(v * 60) })}
      />
      <NumberField
        label="Final adds"
        suffix="min"
        value={toMin(settings.finalSec)}
        min={0.25}
        max={30}
        step={0.25}
        onChange={(v) => onChange({ finalSec: Math.round(v * 60) })}
      />
      <NumberField
        label="Final penalty"
        suffix="life"
        value={settings.penalty}
        min={0}
        max={100}
        step={1}
        onChange={(v) => onChange({ penalty: Math.round(v) })}
      />
      <NumberField
        label="Starting life"
        suffix="life"
        value={settings.startLife}
        min={1}
        max={999}
        step={1}
        onChange={(v) => onChange({ startLife: Math.round(v) })}
      />
      <label className="mt-toggle">
        <input
          type="checkbox"
          checked={settings.sound}
          onChange={(e) => onChange({ sound: e.target.checked })}
        />
        <span>Sound &amp; vibration alerts</span>
      </label>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="mt-overlay" onClick={onClose}>
      <div
        className="mt-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export default function MagicTimer() {
  const [state, dispatch] = useReducer(reducer, undefined, loadState);
  const [showSettings, setShowSettings] = useState(false);
  const [hideWinner, setHideWinner] = useState(false);
  const [, setTickCount] = useState(0); // just forces a re-render while running

  const audioRef = useRef(null);
  const stateRef = useRef(state);
  stateRef.current = state;
  const soundRef = useRef(state.settings.sound);
  soundRef.current = state.settings.sound;
  const lastSecRef = useRef(null);

  const {
    phase,
    settings,
    players,
    turn,
    turnPhase,
    paused,
    deadline,
    remainingMs,
    finalUsed,
    notice,
  } = state;

  const current = players[turn] || players[0];
  const active = turnPhase === 'active';
  const running = phase === 'playing' && active && !paused;
  const totalMs = (settings.startSec + (finalUsed ? settings.finalSec : 0)) * 1000;
  const remaining = !active
    ? settings.startSec * 1000
    : paused
    ? remainingMs
    : Math.max(0, deadline - Date.now());
  const secondsLeft = Math.ceil(remaining / 1000);
  const alive = players.filter((p) => !p.out);
  const winnerPossible =
    phase === 'playing' && players.length > 1 && alive.length <= 1;

  /* ---- audio / vibration ---- */

  // Browsers only allow audio after a tap, so we create the context on the
  // first click anywhere inside the app.
  const unlockAudio = useCallback(() => {
    try {
      if (!audioRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC) audioRef.current = new AC();
      }
      const ctx = audioRef.current;
      if (ctx && ctx.state === 'suspended') ctx.resume();
    } catch (e) {
      /* ignore */
    }
  }, []);

  const alertUser = useCallback((tones, vibration) => {
    if (!soundRef.current) return;
    try {
      const ctx = audioRef.current;
      if (ctx) {
        const t0 = ctx.currentTime;
        tones.forEach(({ f, d, at = 0 }) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'sine';
          osc.frequency.value = f;
          gain.gain.setValueAtTime(0.0001, t0 + at);
          gain.gain.exponentialRampToValueAtTime(0.4, t0 + at + 0.01);
          gain.gain.exponentialRampToValueAtTime(0.0001, t0 + at + d);
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.start(t0 + at);
          osc.stop(t0 + at + d + 0.02);
        });
      }
      if (navigator.vibrate) navigator.vibrate(vibration);
    } catch (e) {
      /* ignore */
    }
  }, []);

  /* ---- clock tick (timestamp based, so background throttling is harmless) ---- */
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => {
      dispatch({ type: 'TICK', now: Date.now() });
      setTickCount((n) => n + 1);
    }, 200);
    return () => clearInterval(id);
  }, [running]);

  /* ---- countdown alerts: 30s, 10s, 3-2-1 ---- */
  useEffect(() => {
    if (!running) {
      lastSecRef.current = null;
      return;
    }
    const prev = lastSecRef.current;
    lastSecRef.current = secondsLeft;
    if (prev === null || prev === secondsLeft) return;
    if (secondsLeft === 30) {
      alertUser([{ f: 660, d: 0.25 }], 200);
    } else if (secondsLeft === 10) {
      alertUser(
        [
          { f: 880, d: 0.15 },
          { f: 880, d: 0.15, at: 0.25 },
        ],
        [120, 80, 120]
      );
    } else if (secondsLeft >= 1 && secondsLeft <= 3) {
      alertUser([{ f: 1000, d: 0.1 }], 60);
    }
  }, [running, secondsLeft, alertUser]);

  /* ---- time's-up / penalty alert + auto-dismiss of the timeout toast ---- */
  useEffect(() => {
    if (!notice) return undefined;
    if (notice.kind === 'penalty') {
      alertUser(
        [
          { f: 330, d: 0.25 },
          { f: 220, d: 0.5, at: 0.3 },
        ],
        [300, 100, 300, 100, 300]
      );
      return undefined;
    }
    alertUser([{ f: 440, d: 0.6 }], 500);
    const id = setTimeout(() => dispatch({ type: 'DISMISS_NOTICE' }), 4000);
    return () => clearTimeout(id);
  }, [notice, alertUser]);

  /* ---- keep the screen awake during a game ---- */
  useEffect(() => {
    if (phase !== 'playing' || !('wakeLock' in navigator)) return undefined;
    let lock = null;
    let cancelled = false;
    const acquire = async () => {
      try {
        const l = await navigator.wakeLock.request('screen');
        if (cancelled) l.release().catch(() => {});
        else lock = l;
      } catch (e) {
        /* not allowed / not supported - ignore */
      }
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') acquire();
    };
    acquire();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      if (lock) lock.release().catch(() => {});
    };
  }, [phase]);

  /* ---- persistence ---- */
  useEffect(() => {
    saveState(state);
  }, [state]);

  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => saveState(stateRef.current), 1000);
    return () => clearInterval(id);
  }, [running]);

  /* ---- misc ---- */
  useEffect(() => {
    if (!winnerPossible) setHideWinner(false);
  }, [winnerPossible]);

  /* ---- actions ---- */
  const send = (type, extra) => dispatch({ type, now: Date.now(), ...extra });

  const restart = () => {
    if (
      window.confirm(
        `Restart the game? Everyone goes back to ${settings.startLife} life.`
      )
    ) {
      send('RESTART');
    }
  };

  const editPlayers = () => {
    if (window.confirm('End this game and go back to player setup?')) {
      send('EDIT_PLAYERS');
    }
  };

  /* ================================================================ */
  /*  Setup screen                                                    */
  /* ================================================================ */
  if (phase === 'setup') {
    return (
      <div className="mt-app" onClick={unlockAudio}>
        <div className="mt-wrap">
          <h1 className="mt-title">Commander Turn Timer</h1>
          <p className="mt-sub">
            Set up the table, then pass the phone around (or prop it in the
            middle).
          </p>

          <section className="mt-panel">
            <div className="mt-row-between">
              <h2 className="mt-h2">Players</h2>
              <div className="mt-stepper">
                <button
                  className="mt-btn mt-btn-small"
                  disabled={players.length <= MIN_PLAYERS}
                  onClick={() => send('SET_COUNT', { count: players.length - 1 })}
                  aria-label="Fewer players"
                >
                  −
                </button>
                <span className="mt-stepper-value">{players.length}</span>
                <button
                  className="mt-btn mt-btn-small"
                  disabled={players.length >= MAX_PLAYERS}
                  onClick={() => send('SET_COUNT', { count: players.length + 1 })}
                  aria-label="More players"
                >
                  +
                </button>
              </div>
            </div>

            {players.map((p, i) => (
              <div
                className="mt-setup-row"
                key={p.id}
                style={{ '--pc': p.color, '--pc-text': textOn(p.color) }}
              >
                <div className="mt-setup-top">
                  <span className="mt-dot" />
                  <input
                    className="mt-input"
                    value={p.name}
                    maxLength={16}
                    placeholder={`Player ${i + 1}`}
                    onChange={(e) =>
                      send('SET_NAME', { id: p.id, name: e.target.value })
                    }
                  />
                </div>
                <div className="mt-swatches">
                  {PALETTE.map((c) => (
                    <button
                      key={c}
                      className={`mt-swatch${c === p.color ? ' sel' : ''}`}
                      style={{ background: c }}
                      onClick={() => send('SET_COLOR', { id: p.id, color: c })}
                      aria-label={`Use color ${c}`}
                    />
                  ))}
                </div>
              </div>
            ))}
            <p className="mt-hint">Turn order follows this list, top to bottom.</p>
          </section>

          <section className="mt-panel">
            <h2 className="mt-h2">Timer &amp; life</h2>
            <SettingsFields
              settings={settings}
              onChange={(patch) => send('SET_SETTINGS', { patch })}
            />
          </section>

          <button
            className="mt-btn mt-btn-lg mt-btn-primary"
            onClick={() => send('START_GAME')}
          >
            Start game
          </button>
        </div>
      </div>
    );
  }

  /* ================================================================ */
  /*  Game screen                                                     */
  /* ================================================================ */
  const pc = current.color;
  const clockClass = [
    'mt-clock',
    active && secondsLeft <= 10 ? 'danger' : active && secondsLeft <= 30 ? 'warn' : '',
    paused ? 'paused' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const pct = active ? Math.min(100, (remaining / totalMs) * 100) : 100;

  return (
    <div className="mt-app" onClick={unlockAudio}>
      <div className="mt-wrap">
        <header className="mt-header">
          <div className="mt-header-title">Commander Timer</div>
          <div className="mt-header-actions">
            <button className="mt-chip" onClick={() => setShowSettings(true)}>
              Settings
            </button>
            <button className="mt-chip" onClick={restart}>
              Restart
            </button>
            <button className="mt-chip" onClick={editPlayers}>
              Players
            </button>
          </div>
        </header>

        {/* ---------- timer ---------- */}
        <section
          className="mt-timer"
          style={{ '--pc': pc, '--pc-text': textOn(pc) }}
        >
          <div className="mt-timer-label">
            {!active ? 'Up next' : paused ? 'Paused' : 'Now playing'}
          </div>
          <div className="mt-timer-name">{current.name}</div>
          <div className={clockClass}>{fmtClock(secondsLeft)}</div>
          <div className="mt-progress">
            <span style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-final-note">
            {finalUsed
              ? `FINAL – lose ${settings.penalty} life if time runs out`
              : '\u00A0'}
          </div>

          {!active ? (
            <div className="mt-actions">
              <button
                className="mt-btn mt-btn-lg"
                onClick={() => send('START_TURN')}
              >
                ▶ Start turn
              </button>
            </div>
          ) : (
            <div className="mt-actions three">
              <button
                className="mt-btn"
                onClick={() => send(paused ? 'RESUME' : 'PAUSE')}
              >
                {paused ? '▶ Resume' : '❚❚ Pause'}
              </button>
              <button
                className="mt-btn"
                disabled={finalUsed}
                onClick={() => send('FINAL')}
              >
                Final +{fmtClock(settings.finalSec)}
              </button>
              <button className="mt-btn" onClick={() => send('END_TURN')}>
                End turn ⏭
              </button>
            </div>
          )}
        </section>

        {/* ---------- players ---------- */}
        <div className="mt-grid">
          {players.map((p, i) => {
            const isCurrent = i === turn && !p.out;
            return (
              <div
                key={p.id}
                className={`mt-player${isCurrent ? ' current' : ''}${
                  p.out ? ' out' : ''
                }`}
                style={{ '--pc': p.color, '--pc-text': textOn(p.color) }}
              >
                <button
                  className="mt-player-name"
                  disabled={turnPhase !== 'waiting' || p.out}
                  onClick={() => send('SET_TURN', { index: i })}
                >
                  {p.name}
                  {isCurrent && <span className="mt-tag">turn</span>}
                </button>
                <div className="mt-life">{p.out ? 'OUT' : p.life}</div>
                <div className="mt-life-btns">
                  <button
                    className="mt-btn"
                    disabled={p.out}
                    onClick={() => send('ADJUST_LIFE', { id: p.id, delta: -1 })}
                  >
                    −1
                  </button>
                  <button
                    className="mt-btn"
                    disabled={p.out}
                    onClick={() => send('ADJUST_LIFE', { id: p.id, delta: 1 })}
                  >
                    +1
                  </button>
                  <button
                    className="mt-btn mt-btn-small"
                    disabled={p.out}
                    onClick={() => send('ADJUST_LIFE', { id: p.id, delta: -5 })}
                  >
                    −5
                  </button>
                  <button
                    className="mt-btn mt-btn-small"
                    disabled={p.out}
                    onClick={() => send('ADJUST_LIFE', { id: p.id, delta: 5 })}
                  >
                    +5
                  </button>
                </div>
                <button
                  className="mt-out-btn"
                  onClick={() => send('TOGGLE_OUT', { id: p.id })}
                >
                  {p.out ? 'Revive' : 'Mark out'}
                </button>
              </div>
            );
          })}
        </div>

        {turnPhase === 'waiting' && (
          <p className="mt-hint">
            Wrong player up? Tap a player's name to change whose turn it is.
          </p>
        )}
      </div>

      {/* ---------- timeout toast ---------- */}
      {notice && notice.kind === 'timeout' && (
        <div className="mt-toast" role="status">
          ⏱ {notice.name}'s time is up – {notice.nextName} is up next.
        </div>
      )}

      {/* ---------- penalty modal ---------- */}
      {notice && notice.kind === 'penalty' && (
        <Modal title="Time penalty">
          <div className="mt-center">
            <div className="mt-emoji">⏰</div>
            <h2 className="mt-h2">{notice.name} ran out of time!</h2>
            <p>
              Final was used, so {notice.name} loses {notice.amount} life.
            </p>
            <div className="mt-penalty-life">
              {notice.from} → {notice.to}
            </div>
            {notice.eliminated && (
              <p className="mt-bad">{notice.name} is eliminated.</p>
            )}
            <button
              className="mt-btn mt-btn-lg mt-btn-primary"
              onClick={() => dispatch({ type: 'DISMISS_NOTICE' })}
            >
              OK – {notice.nextName} is up next
            </button>
          </div>
        </Modal>
      )}

      {/* ---------- winner ---------- */}
      {winnerPossible && !hideWinner && !notice && (
        <Modal title="Game over">
          <div
            className="mt-center"
            style={
              alive[0]
                ? { '--pc': alive[0].color, '--pc-text': textOn(alive[0].color) }
                : undefined
            }
          >
            <div className="mt-emoji">🏆</div>
            <h2 className="mt-h2">
              {alive[0] ? `${alive[0].name} wins!` : 'Everyone is out – draw!'}
            </h2>
            <div className="mt-modal-actions">
              <button
                className="mt-btn mt-btn-lg mt-btn-primary"
                onClick={() => send('RESTART')}
              >
                Play again
              </button>
              <button className="mt-btn" onClick={() => setHideWinner(true)}>
                Keep playing
              </button>
              <button className="mt-btn" onClick={() => send('EDIT_PLAYERS')}>
                Change players
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ---------- settings ---------- */}
      {showSettings && (
        <Modal title="Settings" onClose={() => setShowSettings(false)}>
          <h2 className="mt-h2">Settings</h2>
          <SettingsFields
            settings={settings}
            onChange={(patch) => send('SET_SETTINGS', { patch })}
          />
          <p className="mt-hint">
            Timer changes apply from the next turn. Starting life applies on
            restart.
          </p>
          <button
            className="mt-btn mt-btn-lg mt-btn-primary"
            onClick={() => setShowSettings(false)}
          >
            Done
          </button>
        </Modal>
      )}
    </div>
  );
}